import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";

import {
  Input,
  Row,
  Card,
  Form,
  DatePicker,
  Col,
  Divider,
  Spin,
  Flex,
  Button,
  Tooltip,
  Space,
  InputNumber,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { BOM } from "../../../../../services/definedMethod";
import cnt from "../../../../../services/definedConstant";
import CustomButton from "../../../../../components/custom/CustomButton";
import MainLayout from "../../../../../components/layouts/MainLayout";
import TopContent from "../../../../../components/layouts/TopContent";
import dayjs from "dayjs";

import {
  apiConfig,
  labelConfig,
  staticConfig,
} from "../../../contractbooking.config";
import SuccessModal from "../../../component/SuccessModal";

const { TextArea } = Input;

const AmendnterbankContract = (props) => {
  const { userData, initialValues = null } = props;
  const { session } = userData;
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { state } = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [templateProdDetail, setTemplateProdDetail] = useState({});

  // Initialize state with null values - will be set after API call
  const [interestType, setInterestType] = useState("SI"); // Default fallback
  const [interestRate, setInterestRate] = useState(0);
  const [compoundingFrequency, setCompoundingFrequency] = useState("annually");
  const [tenorDays, setTenorDays] = useState(30); // Default fallback
  const [counterPartyDetails, setCounterPartyDetails] = useState({});
  const [counterPartyExists, setCounterPartyExists] = useState(false);

  // Memoized calculation function
  const calculateMaturityValue = useCallback(() => {
    const amountPlaced = form.getFieldValue("amountPlaced");
    const currentInterestRate =
      form.getFieldValue("interestRate") || interestRate;
    const currentTenor = form.getFieldValue("tenor") || tenorDays;
    const startDate = form.getFieldValue("startDate");

    if (!amountPlaced || !currentInterestRate || !startDate || !currentTenor) {
      return;
    }

    const principal = parseFloat(amountPlaced);
    const rate = parseFloat(currentInterestRate) / 100;
    const tenorInYears = currentTenor / 365;

    let maturityValue = 0;

    if (interestType === "SI") {
      const interest = principal * rate * tenorInYears;
      maturityValue = principal + interest;
    } else if (interestType === "CI") {
      let n = 1;

      switch (compoundingFrequency) {
        case "semiannually":
          n = 2;
          break;
        case "quarterly":
          n = 4;
          break;
        case "monthly":
          n = 12;
          break;
        case "daily":
          n = 365;
          break;
        default:
          n = 1;
      }

      maturityValue = principal * Math.pow(1 + rate / n, n * tenorInYears);
    }

    maturityValue = Math.round(maturityValue * 100) / 100;
    form.setFieldsValue({ maturityValue });
  }, [form, interestRate, tenorDays, interestType, compoundingFrequency]);

  // Update maturity date when tenor changes
  const updateMaturityDate = useCallback(() => {
    const startDate = form.getFieldValue("startDate");
    const currentTenor = form.getFieldValue("tenor") || tenorDays;

    if (startDate && currentTenor) {
      const maturityDate = dayjs(startDate).add(currentTenor, "day");
      form.setFieldsValue({ maturityDate });
    }
  }, [form, tenorDays]);

  // Validator for tenor range
  const validateDefaultTenor = (_, value) => {
    const minTenor = templateProdDetail?.minTenor || 1;
    const maxTenor = templateProdDetail?.maxTenor || 365;

    if (value < minTenor) {
      return Promise.reject(`Tenor cannot be less than ${minTenor} days`);
    }
    if (value > maxTenor) {
      return Promise.reject(`Tenor cannot be greater than ${maxTenor} days`);
    }
    return Promise.resolve();
  };

  // Handle tenor change
  const handleTenorChange = (value) => {
    setTenorDays(value);
    // Trigger calculations after state update
    setTimeout(() => {
      updateMaturityDate();
      calculateMaturityValue();
    }, 0);
  };

  // Handle interest rate change
  const handleInterestRateChange = (value) => {
    setInterestRate(value);
    // Trigger calculation after state update
    setTimeout(() => {
      calculateMaturityValue();
    }, 0);
  };

  // Initial API call
  useEffect(() => {
    if (state?.contractObj?.product?.id) {
      getInterBankProductDetails(state?.contractObj);
      getClientCounterParty(state);
    }
  }, [state]);

  // Recalculate when template data is loaded
  useEffect(() => {
    console.log(state);

    if (templateProdDetail.id) {
      updateMaturityDate();
      calculateMaturityValue();
    }
  }, [templateProdDetail, updateMaturityDate, calculateMaturityValue]);

  const getInterBankProductDetails = (contractParam) => {
    const url = `${apiConfig.InterBankPlacementProducts}/${contractParam?.product?.id}`;
    let body = { session };

    setIsLoading(true);

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
        setIsLoading(false);
      } else {
        setTemplateProdDetail(res);
        const objList = res;

        console.log("Template Product Details:", objList);

        // Update state variables based on API response
        const apiInterestType = objList?.interestType?.text;
        let newInterestType = "SI";
        if (apiInterestType === "Simple Interest") {
          newInterestType = "SI";
        } else if (apiInterestType === "Compound Interest") {
          newInterestType = "CI";
        }
        setInterestType(newInterestType);

        const defaultRate = contractParam?.interestRate ?? objList?.defaultRate;
        const defaultTenor = contractParam?.tenor || objList?.defaultTenor;

        setInterestRate(defaultRate);
        setTenorDays(defaultTenor);

        // Set all form values including the interactive fields
        form.setFieldsValue({
          // Contract Information
          productCode: objList?.productCode || "",
          productName: objList?.productName || "",
          productType: objList?.productType?.text || "",
          currencyCode: objList?.currencyCode || "",

          // Terms
          decimalPlaces: objList?.currency?.decimalPlaces || 0,
          inMultiplesOf: objList?.currency?.inMultiplesOf || 1,
          submittedOnDate: dayjs(),
          nominalAnnualInterestRate: objList?.nominalAnnualInterestRate || 0,

          // Interactive fields that users can modify
          interestRate: defaultRate,
          // interestRate: contractParam?.interestRate || defaultRate,
          // tenor: contractParam?.tenor || defaultTenor,
          tenor: defaultTenor,

          // Terms Configuration
          minTenor: objList?.minTenor || 0,
          maxTenor: objList?.maxTenor || 0,
          allowRollover: objList?.allowRollover || false,
          autoRollover: objList?.autoRollover || false,
          backdatedValueDateAllowed:
            objList?.backdatedValueDateAllowed || false,
          allowEarlyTermination: objList?.allowEarlyTermination || false,
          earlyTerminationWithPenalty:
            objList?.earlyTerminationWithPenalty || false,
          maturityDateAdjustment: objList?.maturityDateAdjustment?.value || "",
          allowPlacementFee: objList?.allowPlacementFee || false,
          placementFeeWithPenalty: objList?.placementFeeWithPenalty || false,

          // Interest Configuration
          interestType: objList?.interestType?.value || "",
          interestRateType: objList?.interestRateType?.text || "",
          interestCalculationBasis:
            objList?.interestCalculationBasis?.text || "",
          interestPostingType: objList?.interestPostingType?.text || "",
          allowWithholdingTax: objList?.allowWithholdingTax || false,
          withholdingTax: objList?.withholdingTax || 0,
          withholdingTaxCharges: objList?.withholdingTax?.name || "",
        });

        setIsLoading(false);
      }
    });
  };

  const getClientCounterParty = (state) => {
    setIsLoading(true);

    let body = {
      session,
    };

    const url = `${apiConfig.Counterparty}/${state.customerId}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        // setIsLoading(false);
        setCounterPartyExists(false);
        setCounterPartyDetails(null);
        // BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
      } else {
        // setIsLoading(false);
        setCounterPartyExists(true);
        const objList = res;
        setCounterPartyDetails(objList);
      }
    });
  };

  function onCancel() {
    navigate("/view-interbank-contract", { state: state });
  }

  const onSubmit = (formValues) => {
    console.log("Form values being submitted:", formValues);

    const payloadData = {};

    payloadData.productId = templateProdDetail.id;
    payloadData.clientId = state.customerId;
    payloadData.counterpartyId = counterPartyDetails.id;
    payloadData.submittedOnDate = BOM.DateTime(
      templateProdDetail.startDate,
      "only-date"
    );
    payloadData.interestRateType = templateProdDetail.interestRateType?.value;
    payloadData.interestRate = formValues.interestRate;
    payloadData.tenor = formValues.tenor;
    payloadData.amount = templateProdDetail.riskLimit;
    payloadData.allowRollover = templateProdDetail.allowRollover;
    payloadData.autoRollover = templateProdDetail.autoRollover;
    payloadData.dealConfirmation = templateProdDetail.activateDealConfirmation;

    const url = `${apiConfig.InterbankContract}/${state.id}`;

    const body = {
      // ...formValues,
      ...payloadData,
      dateFormat: "dd MMMM yyyy",
      locale: "en",
      method: "PUT",
      session,
    };

    console.log("Submission body:", body);

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage);
        setIsLoading(false);
      } else {
        setIsModalVisible(true);
        setIsLoading(false);
      }
    });
  };

  const handleGoBack = () => {
    navigate("/view-interbank-contract", {
      state: { ...state, statusValue: "approved" },
    });
  };

  // Get min/max values for validation
  const minTenor = templateProdDetail?.minTenor || 1;
  const maxTenor = templateProdDetail?.maxTenor || 365;

  return (
    <MainLayout isEnableRightSidebar={true}>
      <TopContent pageTitle={"Amend Deal"} pageItems={[]} />

      <Spin spinning={isLoading} size="large">
        <Card
          headStyle={{ borderBottomWidth: 0 }}
          style={{
            boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
            borderRadius: 20,
          }}
        >
          <Form form={form} layout="vertical" onFinish={onSubmit}>
            <Row>
              <Divider orientation="left">
                {`Contract Ref ID: ${state.contractRefId}`}
              </Divider>

              <Col span={12} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={
                    <Space>
                      Interest Rate
                      <Tooltip title="Annual interest rate percentage">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name="interestRate"
                  rules={[
                    {
                      required: true,
                      message: "Please enter Rate Value",
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%", height: "44px" }}
                    placeholder="0.00"
                    suffix="%"
                    min={0}
                    max={100}
                    step={0.01}
                    onChange={handleInterestRateChange}
                  />
                </Form.Item>
              </Col>

              {/* Tenor Field - User can modify */}
              <Col span={12} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={
                    <Space>
                      Tenor (Days)
                      <Tooltip
                        title={`Term period (${minTenor}-${maxTenor} days)`}
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name="tenor"
                  rules={[
                    {
                      required: true,
                      message: "Please enter tenor",
                    },
                    {
                      validator: validateDefaultTenor,
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%", height: "44px" }}
                    placeholder="30"
                    suffix="days"
                    min={minTenor}
                    max={maxTenor}
                    onChange={handleTenorChange}
                  />
                </Form.Item>
              </Col>

              {/* Display calculated maturity value - read-only */}
              {/* <Col span={12} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={
                    <Space>
                      Maturity Value
                      <Tooltip title="Calculated maturity value based on interest and tenor">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name="maturityValue"
                >
                  <InputNumber
                    style={{ width: "100%", height: "44px" }}
                    placeholder="0.00"
                    disabled
                    formatter={(value) =>
                      `${
                        templateProdDetail?.currencyCode || ""
                      } ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                  />
                </Form.Item>
              </Col> */}

              {/* Display maturity date - read-only */}
              {/* <Col span={12} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={
                    <Space>
                      Maturity Date
                      <Tooltip title="Calculated maturity date based on start date and tenor">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  name="maturityDate"
                >
                  <DatePicker
                    style={{ width: "100%", height: "44px" }}
                    disabled
                    format="DD MMMM YYYY"
                  />
                </Form.Item>
              </Col> */}
            </Row>

            <Divider />

            <div style={{ paddingTop: 20, borderRadius: 20 }}>
              <Flex justify="end" gap="10px" align="end">
                <Button
                  style={{
                    backgroundColor: "white",
                    borderRadius: 8,
                    border: "1px solid #D0D5DD",
                    padding: "20px 15px",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                  onClick={onCancel}
                >
                  Cancel
                </Button>
                <Button
                  style={{
                    backgroundColor: "#15CCAE",
                    color: "white",
                    border: "1px solid #BA24D5",
                    borderRadius: 8,
                    padding: "20px 15px",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                  htmlType="submit"
                >
                  Submit
                </Button>
              </Flex>
            </div>
          </Form>
        </Card>

        <SuccessModal
          visible={isModalVisible}
          onClose={handleGoBack}
          successMessage={"Contract Amended successfully"}
        />
      </Spin>
    </MainLayout>
  );
};

export default AmendnterbankContract;
